import { useState } from "react";
import { FieldValues, UseControllerProps } from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Box from "@mui/material/Box";
import ListItem from "@mui/material/ListItem";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";

import AutocompleteFormField from "components/autocomplete-form-field";
import {
  ProductsAccount,
  useGetProductsAccountsQuery
} from "services/external-accounts";

type ExternalAccountFieldProps = {
  label?: string;
  required?: boolean;
  disabled?: boolean;
  onOptionSelected?: (option: ProductsAccount | null) => void;
  sx?: SxProps;
  placeholder?: string;
};

export type RhfExternalAccountField<T extends FieldValues> =
  ExternalAccountFieldProps & UseControllerProps<T>;

export default function ExternalAccountField<T extends FieldValues>({
  control,
  required,
  disabled,
  name,
  label = "External Account",
  onOptionSelected,
  sx = {},
  placeholder = "Search External Account"
}: RhfExternalAccountField<T>) {
  const [search, setSearch] = useState<string>("");

  const { data: externalAccounts = [], isLoading } =
    useGetProductsAccountsQuery();

  const filteredExternalAccounts =
    search === ""
      ? externalAccounts || []
      : (externalAccounts || []).filter((item) =>
          item.name.toLowerCase().includes(search.toLowerCase())
        );

  return (
    <AutocompleteFormField
      isLoading={isLoading}
      required={required}
      disabled={disabled}
      openOnFocus
      name={name}
      label={label}
      options={filteredExternalAccounts}
      noOptionsText={!search?.length ? placeholder : "No options"}
      getOptionLabel={({ name }: ProductsAccount) => name}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      placeholder={placeholder}
      renderOption={(props: object, option: ProductsAccount) => (
        <ListItem {...props}>
          <Stack direction="row" spacing={2}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                fontSize: 18
              }}
            >
              {option.name}
            </Box>
            <Box sx={{ fontSize: 16, lineHeight: 1.3 }}>
              {option.external_id}
            </Box>
          </Stack>
        </ListItem>
      )}
      popupIcon={<ArrowDropDownIcon />}
      control={control}
      onTextInputChange={(text) => {
        setSearch(text);
      }}
      onOptionSelected={(option: any) => {
        if (!option) {
          setSearch("");
        }
        if (onOptionSelected) {
          onOptionSelected(option);
        }
      }}
      sx={sx}
    />
  );
}
